import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import { apiRequest } from '../api';
import '../css/integrations.css';

// Tooltip component for better UX
const Tooltip = ({ children, content }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div 
            className="tooltip-container"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && (
                <div className="tooltip">
                    {content}
                </div>
            )}
        </div>
    );
};

const IntegrationTemplateManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const [formData, setFormData] = useState({
        name: '',
        display_name: '',
        description: '',
        version: '1.0.0',
        is_active: true,
        configuration: {
            authentication: {
                type: '',
                fields: [],
                description: ''
            },
            endpoints: {
                base_url: '',
                submit_data: {
                    url: '',
                    method: '',
                    content_type: ''
                },
                test_connection: {
                    url: '',
                    method: ''
                }
            },
            field_mappings: {
                mapping_type: '',
                description: '',
                required_fields: [],
                optional_fields: [],
                field_mappings: {}, // For field name mapping scenario
                core_fields: [], // For core plus extra scenario
                extra_field_name: 'extra_args' // For core plus extra scenario
            },
            sync_settings: {
                batch_size: 10,
                retry_attempts: 1,
                timeout_seconds: 30,
                rate_limit: {
                    requests_per_minute: 60
                }
            },
            error_handling: {
                retry_on_failure: true,
                log_errors: true,
                notify_on_failure: false
            }
        }
    });
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    // Mapping type descriptions for tooltips
    const mappingTypeDescriptions = {
        'direct_match': 'Direct field matching - maps form fields directly to CRM fields with the same names. Best for simple integrations where field names match exactly.',
        'core_plus_extra': 'Core fields + Extra args - sends core fields directly and bundles remaining fields into an extra_args object. Perfect for CRMs with specific required fields.',
        'core_plus_extra_mapped': 'Core fields + Extra args with Field Mapping - sends core fields with custom CRM field names and bundles remaining fields into an extra_args object. Ideal for CRMs with specific required fields that need custom field names.',
        'field_name_mapping': 'Field name mapping - maps form field names to different CRM field names. Ideal for complex integrations with different naming conventions.'
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await apiRequest(`${apiUrl}/integration-templates/`, 'GET', null, token);
            setTemplates(response.templates || []);
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTemplate = async () => {
        try {
            setErrors({});
            
            // Validate required fields
            const validationErrors = {};
            if (!formData.name || !formData.name.trim()) {
                validationErrors.name = 'Name is required';
            }
            if (!formData.display_name || !formData.display_name.trim()) {
                validationErrors.display_name = 'Display name is required';
            }
            if (!formData.configuration?.field_mappings?.mapping_type) {
                validationErrors.field_mappings = 'Mapping type is required';
            }
            
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }
            
            const token = localStorage.getItem("token");
            console.log('Creating template with data:', formData);
            const response = await apiRequest(`${apiUrl}/integration-templates/`, 'POST', formData, token);
            
            // The backend returns the template object directly on success
            if (response && response.id) {
                setShowCreateModal(false);
                resetFormData();
                fetchTemplates();
                // Show success message
                setSuccessMessage('Template created successfully!');
                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                console.error('API response error:', response);
                setErrors({ general: 'Failed to create template - invalid response' });
            }
        } catch (error) {
            console.error('Error creating template:', error);
            // Check if it's a network error or server error
            if (error.response) {
                // Server responded with error status
                const errorData = error.response.data || {};
                setErrors({ 
                    general: errorData.detail || `Server error: ${error.response.status}` 
                });
            } else if (error.request) {
                // Network error
                setErrors({ general: 'Network error: Unable to connect to server' });
            } else {
                // Other error
                setErrors({ general: 'Failed to create template: ' + (error.message || 'Unknown error') });
            }
        }
    };

    const handleUpdateTemplate = async () => {
        try {
            setErrors({});
            
            // Validate required fields
            const validationErrors = {};
            if (!formData.name || !formData.name.trim()) {
                validationErrors.name = 'Name is required';
            }
            if (!formData.display_name || !formData.display_name.trim()) {
                validationErrors.display_name = 'Display name is required';
            }
            if (!formData.configuration?.field_mappings?.mapping_type) {
                validationErrors.field_mappings = 'Mapping type is required';
            }
            
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }
            
            const token = localStorage.getItem("token");
            console.log('Updating template with data:', formData);
            const response = await apiRequest(`${apiUrl}/integration-templates/${selectedTemplate.id}`, 'PUT', formData, token);
            
            // The backend returns the updated template object directly on success
            if (response && response.id) {
                setShowEditModal(false);
                setSelectedTemplate(null);
                fetchTemplates();
                // Show success message
                setSuccessMessage('Template updated successfully!');
                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                console.error('API response error:', response);
                setErrors({ general: 'Failed to update template - invalid response' });
            }
        } catch (error) {
            console.error('Error updating template:', error);
            // Check if it's a network error or server error
            if (error.response) {
                // Server responded with error status
                const errorData = error.response.data || {};
                setErrors({ 
                    general: errorData.detail || `Server error: ${error.response.status}` 
                });
            } else if (error.request) {
                // Network error
                setErrors({ general: 'Network error: Unable to connect to server' });
            } else {
                // Other error
                setErrors({ general: 'Failed to update template: ' + (error.message || 'Unknown error') });
            }
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            try {
                const token = localStorage.getItem("token");
                const response = await apiRequest(`${apiUrl}/integration-templates/${templateId}`, 'DELETE', null, token);
                // The backend returns a message object on successful deletion
                if (response && response.message) {
                    fetchTemplates();
                    // Show success message
                    setSuccessMessage('Template deleted successfully!');
                    // Clear success message after 3 seconds
                    setTimeout(() => setSuccessMessage(''), 3000);
                }
            } catch (error) {
                console.error('Error deleting template:', error);
            }
        }
    };

    const openEditModal = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            display_name: template.display_name,
            description: template.description,
            version: template.version,
            is_active: template.is_active,
            configuration: template.configuration
        });
        setShowEditModal(true);
    };

    const resetFormData = () => {
        setFormData({
            name: 'slate_crm_template',
            display_name: 'Slate CRM Template',
            description: 'Template for Slate CRM integration with basic authentication and data mapping',
            version: '1.0.0',
            is_active: true,
            configuration: {
                authentication: {
                    type: 'basic_auth',
                    fields: ['username', 'password'],
                    description: 'Basic authentication for Slate CRM access'
                },
                endpoints: {
                    base_url: 'https://your-slate-instance.slate.com',
                    submit_data: {
                        url: '/api/v1/prospects',
                        method: 'POST',
                        content_type: 'application/json'
                    },
                    test_connection: {
                        url: '/api/v1/test',
                        method: 'GET'
                    }
                },
                field_mappings: {
                    mapping_type: 'direct_match',
                    description: 'Direct field matching for Slate CRM',
                    required_fields: ['First Name', 'Last Name', 'Email'],
                    optional_fields: ['Phone', 'Address', 'Notes'],
                    field_mappings: {},
                    core_fields: [],
                    core_field_mappings: {},
                    extra_field_name: 'extra_args'
                },
                sync_settings: {
                    mode: 'single',
                    batch_size: 50,
                    interval_minutes: 15,
                    records_per_interval: 15,
                    rate_limit: {
                        requests_per_minute: 60
                    }
                },
                error_handling: {
                    retry_on_failure: true,
                    log_errors: true,
                    notify_on_failure: false
                }
            }
        });
        setErrors({});
    };

    const openViewModal = (template) => {
        setSelectedTemplate(template);
        setShowViewModal(true);
    };

    const updateConfiguration = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [section]: {
                    ...prev.configuration[section],
                    [field]: value
                }
            }
        }));
    };

    const updateNestedConfiguration = (section, subsection, field, value) => {
        setFormData(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [section]: {
                    ...prev.configuration[section],
                    [subsection]: {
                        ...prev.configuration[section][subsection],
                        [field]: value
                    }
                }
            }
        }));
    };

    const duplicateTemplate = (template) => {
        // Prepare a copy of the template data for duplication
        const duplicatedConfig = JSON.parse(JSON.stringify(template.configuration)); // deep copy
        
        // Ensure field mappings have the correct structure
        if (duplicatedConfig.field_mappings) {
            // Convert type to mapping_type if needed
            if (duplicatedConfig.field_mappings.type && !duplicatedConfig.field_mappings.mapping_type) {
                duplicatedConfig.field_mappings.mapping_type = duplicatedConfig.field_mappings.type;
            }
            
            // Ensure all required fields exist
            duplicatedConfig.field_mappings = {
                mapping_type: duplicatedConfig.field_mappings.mapping_type || duplicatedConfig.field_mappings.type || 'direct_match',
                description: duplicatedConfig.field_mappings.description || '',
                required_fields: duplicatedConfig.field_mappings.required_fields || [],
                optional_fields: duplicatedConfig.field_mappings.optional_fields || [],
                field_mappings: duplicatedConfig.field_mappings.field_mappings || duplicatedConfig.field_mappings.mappings || {},
                core_fields: duplicatedConfig.field_mappings.core_fields || [],
                core_field_mappings: duplicatedConfig.field_mappings.core_field_mappings || {},
                extra_field_name: duplicatedConfig.field_mappings.extra_field_name || 'extra_args'
            };
        }
        
        setFormData({
            name: template.name + '_copy',
            display_name: template.display_name + ' (Copy)',
            description: template.description,
            version: template.version,
            is_active: template.is_active,
            configuration: duplicatedConfig,
        });
        setShowCreateModal(true);
    };

    const openDeleteModal = (template) => {
        setTemplateToDelete(template);
        setDeleteConfirmText("");
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        setDeleteConfirmText("");
    };

    const confirmDeleteTemplate = async () => {
        if (!templateToDelete) return;
        try {
            const token = localStorage.getItem("token");
            const response = await apiRequest(`${apiUrl}/integration-templates/${templateToDelete.id}`, 'DELETE', null, token);
            // The backend returns a message object on successful deletion
            if (response && response.message) {
                fetchTemplates();
                // Show success message
                setSuccessMessage('Template deleted successfully!');
                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
        } finally {
            closeDeleteModal();
        }
    };

    // Render field mapping configuration based on mapping type
    const renderFieldMappingConfig = () => {
        const mappingType = formData.configuration.field_mappings.mapping_type;
        
        return (
            <div className="configuration-section">
                <h3>
                    Field Mappings
                                            <Tooltip content="Configure how form data is mapped to CRM fields">
                            <span style={{ marginLeft: '8px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                </h3>
                <div className="form-group">
                    <label>
                        Mapping Type
                        <Tooltip content="Choose how form fields are mapped to CRM fields">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                    </label>
                    <select
                        value={mappingType}
                        onChange={(e) => {
                            updateConfiguration('field_mappings', 'mapping_type', e.target.value);
                            // Reset field mappings when type changes
                            updateConfiguration('field_mappings', 'required_fields', []);
                            updateConfiguration('field_mappings', 'optional_fields', []);
                            updateConfiguration('field_mappings', 'field_mappings', {});
                            updateConfiguration('field_mappings', 'core_fields', []);
                            updateConfiguration('field_mappings', 'core_field_mappings', {});
                        }}
                    >
                        <option value="">Select mapping type</option>
                        <option value="direct_match">Direct Match</option>
                        <option value="core_plus_extra">Core + Extra</option>
                        <option value="core_plus_extra_mapped">Core + Extra with Field Mapping</option>
                        <option value="field_name_mapping">Field Name Mapping</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Description</label>
                    <input
                        type="text"
                        value={formData.configuration.field_mappings.description}
                        onChange={(e) => updateConfiguration('field_mappings', 'description', e.target.value)}
                        placeholder="Describe the field mapping approach..."
                    />
                </div>

                {mappingType === 'direct_match' && (
                    <div>
                        <div className="form-group">
                            <label>
                                Required Fields
                                            <Tooltip content="Form fields that must be present (comma-separated)">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings.required_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'required_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="First Name, Last Name, Email (comma-separated)"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Optional Fields
                                            <Tooltip content="Form fields that may be present (comma-separated)">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings.optional_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'optional_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="Phone, Address, Notes (comma-separated)"
                            />
                        </div>
                    </div>
                )}

                {mappingType === 'core_plus_extra' && (
                    <div>
                        <div className="form-group">
                            <label>
                                Core Fields
                                            <Tooltip content="Fields that will be sent directly to CRM (comma-separated)">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings.core_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'core_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="First Name, Last Name, Email (comma-separated)"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Extra Field Name
                                                        <Tooltip content="Name of the field that will contain all non-core fields">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings.extra_field_name}
                                onChange={(e) => updateConfiguration('field_mappings', 'extra_field_name', e.target.value)}
                                placeholder="extra_args"
                            />
                        </div>
                    </div>
                )}

                {mappingType === 'core_plus_extra_mapped' && (
                    <div>
                        <div className="form-group">
                            <label>
                                Core Field Mappings
                                                        <Tooltip content="Map core form fields to specific CRM field names (form_field:crm_field)">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <textarea
                                value={Object.entries(formData.configuration.field_mappings.core_field_mappings || {})
                                    .map(([key, value]) => `${key}:${value}`)
                                    .join('\n')}
                                onChange={(e) => {
                                    const mappings = {};
                                    e.target.value.split('\n').forEach(line => {
                                        const [key, value] = line.split(':').map(s => s.trim());
                                        if (key && value) {
                                            mappings[key] = value;
                                        }
                                    });
                                    updateConfiguration('field_mappings', 'core_field_mappings', mappings);
                                }}
                                placeholder="First Name:first_name\nLast Name:last_name\nEmail:email_address"
                                rows={5}
                                style={{ width: '100%', padding: '8px' }}
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Extra Field Name
                                                        <Tooltip content="Name of the field that will contain all non-core fields">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings.extra_field_name}
                                onChange={(e) => updateConfiguration('field_mappings', 'extra_field_name', e.target.value)}
                                placeholder="extra_args"
                            />
                        </div>
                    </div>
                )}

                {mappingType === 'field_name_mapping' && (
                    <div>
                        <div className="form-group">
                            <label>
                                Field Mappings
                                                        <Tooltip content="Map form field names to CRM field names (form_field:crm_field)">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <textarea
                                value={Object.entries(formData.configuration.field_mappings.field_mappings || {})
                                    .map(([key, value]) => `${key}:${value}`)
                                    .join('\n')}
                                onChange={(e) => {
                                    const mappings = {};
                                    e.target.value.split('\n').forEach(line => {
                                        const [key, value] = line.split(':').map(s => s.trim());
                                        if (key && value) {
                                            mappings[key] = value;
                                        }
                                    });
                                    updateConfiguration('field_mappings', 'field_mappings', mappings);
                                }}
                                placeholder="First Name:first_name\nLast Name:last_name\nEmail:email_address"
                                rows={5}
                                style={{ width: '100%', padding: '8px' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderAuthenticationFields = () => {
        const authType = formData.configuration.authentication.type;
        
        return (
            <div className="configuration-section">
                <h3>Authentication</h3>
                <div className="form-group">
                    <label>Type
                        <Tooltip content="Select the authentication method required by the external system (e.g., Basic Auth, API Key, OAuth2, or None)">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                    </label>
                    <select
                        value={authType}
                        onChange={(e) => {
                            updateConfiguration('authentication', 'type', e.target.value);
                            // Reset authentication fields when type changes
                            updateConfiguration('authentication', 'username', '');
                            updateConfiguration('authentication', 'password', '');
                            updateConfiguration('authentication', 'api_key', '');
                            updateConfiguration('authentication', 'header_name', 'X-API-Key');
                            updateConfiguration('authentication', 'client_id', '');
                            updateConfiguration('authentication', 'client_secret', '');
                            updateConfiguration('authentication', 'access_token', '');
                            updateConfiguration('authentication', 'token_url', '');
                            updateConfiguration('authentication', 'scope', '');
                        }}
                    >
                        <option value="">Select authentication type</option>
                        <option value="basic_auth">Basic Auth</option>
                        <option value="api_key">API Key</option>
                        <option value="oauth2">OAuth 2.0</option>
                        <option value="none">None</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Description
                        <Tooltip content="Describe the authentication requirements for this integration (e.g., 'Requires API key in header')">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                    </label>
                    <input
                        type="text"
                        value={formData.configuration.authentication.description}
                        onChange={(e) => updateConfiguration('authentication', 'description', e.target.value)}
                        placeholder="Authentication description..."
                    />
                </div>

                {/* Dynamic authentication fields based on type */}
                {authType === 'basic_auth' && (
                    <div>
                        <div className="form-group">
                            <label>
                                Username Field Name
                                                        <Tooltip content="The username for authenticating with the external system.">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.username || 'username'}
                                onChange={(e) => updateConfiguration('authentication', 'username', e.target.value)}
                                placeholder="username"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Password Field Name
                                                        <Tooltip content="The password or secret for authenticating with the external system.">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.password || 'password'}
                                onChange={(e) => updateConfiguration('authentication', 'password', e.target.value)}
                                placeholder="password"
                            />
                        </div>
                    </div>
                )}

                {authType === 'api_key' && (
                    <div>
                        <div className="form-group">
                            <label>
                                API Key Field Name
                                                        <Tooltip content="The API key provided by the external system for authentication.">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.api_key || 'api_key'}
                                onChange={(e) => updateConfiguration('authentication', 'api_key', e.target.value)}
                                placeholder="api_key"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Header Name
                                                        <Tooltip content="The HTTP header name where the API key should be sent (e.g., 'X-API-Key').">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.header_name || 'X-API-Key'}
                                onChange={(e) => updateConfiguration('authentication', 'header_name', e.target.value)}
                                placeholder="X-API-Key"
                            />
                        </div>
                    </div>
                )}

                {authType === 'oauth2' && (
                    <div>
                        <div className="form-group">
                            <label>
                                Client ID Field Name
                                                        <Tooltip content="The OAuth2 client ID provided by the external system.">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.client_id || 'client_id'}
                                onChange={(e) => updateConfiguration('authentication', 'client_id', e.target.value)}
                                placeholder="client_id"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Client Secret Field Name
                                                        <Tooltip content="The OAuth2 client secret provided by the external system.">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.client_secret || 'client_secret'}
                                onChange={(e) => updateConfiguration('authentication', 'client_secret', e.target.value)}
                                placeholder="client_secret"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Access Token Field Name
                                                        <Tooltip content="The OAuth2 access token for API requests.">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.access_token || 'access_token'}
                                onChange={(e) => updateConfiguration('authentication', 'access_token', e.target.value)}
                                placeholder="access_token"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Token URL
                                                        <Tooltip content="The endpoint to obtain OAuth2 tokens (e.g., https://oauth.example.com/token).">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="url"
                                value={formData.configuration.authentication.token_url || ''}
                                onChange={(e) => updateConfiguration('authentication', 'token_url', e.target.value)}
                                placeholder="https://oauth.example.com/token"
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                Scope
                                                        <Tooltip content="The OAuth2 scope (space-separated, optional).">
                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                        </Tooltip>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication.scope || ''}
                                onChange={(e) => updateConfiguration('authentication', 'scope', e.target.value)}
                                placeholder="read write"
                            />
                        </div>
                    </div>
                )}

                {authType === 'none' && (
                    <div className="form-group">
                        <p style={{ color: '#666', fontStyle: 'italic' }}>
                            No authentication required for this integration.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <div className="main-content">
                    <DashboardHeader />
                    <div className="content">
                        <div className="loading">Loading templates...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <div className="main-content">
                <header className="header component">
                    <span className="component-header">Integration Templates</span>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            resetFormData();
                            setShowCreateModal(true);
                        }}
                    >
                        <i className="fas fa-plus"></i> Create Template
                    </button>
                </header>
                <p><a href="/dashboard"> Home </a> &gt; Integration Templates </p>
                <hr/>
                
                {/* Success Message */}
                {successMessage && (
                    <div style={{
                        backgroundColor: '#d4edda',
                        color: '#155724',
                        padding: '12px 16px',
                        borderRadius: '4px',
                        marginBottom: '20px',
                        border: '1px solid #c3e6cb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{successMessage}</span>
                        <button
                            onClick={() => setSuccessMessage('')}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#155724',
                                fontSize: '18px',
                                cursor: 'pointer',
                                padding: '0',
                                marginLeft: '10px'
                            }}
                        >
                            ×
                        </button>
                    </div>
                )}
                
                <div className="content">


                    <div className="templates-grid">
                    {templates.map(template => (
                            <div key={template.id} className="template-card">
                                <div className="template-header">
                                    <h3>{template.display_name}</h3>
                                    <span className={`status ${template.is_active ? 'active' : 'inactive'}`}>
                                        {template.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <p className="template-description">{template.description}</p>
                                <div className="template-meta">
                                    <span className="version">v{template.version}</span>
                                    <span className="type">{template.configuration?.authentication?.type || 'Unknown'}</span>
                                </div>
                                <div className="template-actions">
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => openViewModal(template)}
                                    >
                                        <i className="fas fa-eye"></i> View
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => openEditModal(template)}
                                    >
                                        <i className="fas fa-edit"></i> Edit
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => duplicateTemplate(template)}
                                    >
                                        <i className="fas fa-copy"></i> Duplicate
                                    </button>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => openDeleteModal(template)}
                                    >
                                        <i className="fas fa-trash"></i> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {templates.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-cube"></i>
                            <h3>No Integration Templates</h3>
                            <p>Create your first integration template to get started.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    resetFormData();
                                    setShowCreateModal(true);
                                }}
                            >
                                Create Template
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Template Modal */}
            {showCreateModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            padding: "20px",
                            borderRadius: "5px",
                            width: "80%",
                            maxWidth: "800px",
                            maxHeight: "90vh",
                            overflow: "auto",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                            <h2>Create Integration Template</h2>
                            <button 
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "20px",
                                    cursor: "pointer",
                                    padding: "5px"
                                }}
                                onClick={() => setShowCreateModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div>
                            <div className="form-group">
                                <label>Template Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="e.g., slate_crm"
                                />
                                {errors.name && <span className="error">{errors.name}</span>}
                            </div>
                            <div className="form-group">
                                <label>Display Name *</label>
                                <input
                                    type="text"
                                    value={formData.display_name}
                                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                                    placeholder="e.g., Slate CRM Integration"
                                />
                                {errors.display_name && <span className="error">{errors.display_name}</span>}
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="Describe this integration template..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Version</label>
                                <input
                                    type="text"
                                    value={formData.version}
                                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                                    placeholder="1.0.0"
                                />
                            </div>
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>

                            {/* Configuration Sections */}
                            {renderAuthenticationFields()}

                            <div className="configuration-section">
                                <h3>Endpoints</h3>
                                <div className="form-group">
                                    <label>Base URL</label>
                                    <input
                                        type="url"
                                        value={formData.configuration.endpoints.base_url}
                                        onChange={(e) => updateConfiguration('endpoints', 'base_url', e.target.value)}
                                        placeholder="https://api.example.com"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Submit Data URL</label>
                                    <input
                                        type="text"
                                        value={formData.configuration.endpoints.submit_data.url}
                                        onChange={(e) => updateNestedConfiguration('endpoints', 'submit_data', 'url', e.target.value)}
                                        placeholder="/submit"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Test Connection URL</label>
                                    <input
                                        type="text"
                                        value={formData.configuration.endpoints.test_connection.url}
                                        onChange={(e) => updateNestedConfiguration('endpoints', 'test_connection', 'url', e.target.value)}
                                        placeholder="/test"
                                    />
                                </div>
                            </div>

                            {renderFieldMappingConfig()}

                            <div className="configuration-section">
                                <h3>
                                    Sync Settings
                                    <Tooltip content="Configure how data is synchronized with the CRM system">
                                        <span style={{ marginLeft: '8px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                    </Tooltip>
                                </h3>
                                
                                <div className="form-group">
                                    <label>
                                        Sync Mode
                                        <Tooltip content="Choose between single record processing or batch processing">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <select
                                        className="form-control"
                                        value={formData.configuration.sync_settings.mode || 'single'}
                                        onChange={(e) => updateConfiguration('sync_settings', 'mode', e.target.value)}
                                    >
                                        <option value="single">Single Record</option>
                                        <option value="batch">Batch Processing</option>
                                    </select>
                                </div>

                                {formData.configuration.sync_settings.mode === 'batch' && (
                                    <div className="form-group">
                                        <label>
                                            Batch Size
                                            <Tooltip content="Number of records to process in each batch">
                                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                            </Tooltip>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.configuration.sync_settings.batch_size || 50}
                                            onChange={(e) => updateConfiguration('sync_settings', 'batch_size', parseInt(e.target.value) || 50)}
                                            min="1"
                                            max="1000"
                                            placeholder="50"
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>
                                        Interval (minutes)
                                        <Tooltip content="How often to run sync operations">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.configuration.sync_settings.interval_minutes || 15}
                                        onChange={(e) => updateConfiguration('sync_settings', 'interval_minutes', parseInt(e.target.value) || 15)}
                                        min="1"
                                        max="1440"
                                        placeholder="15"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        Records Per Interval
                                        <Tooltip content="Maximum number of records to process per sync interval">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.configuration.sync_settings.records_per_interval || ''}
                                        onChange={(e) => updateConfiguration('sync_settings', 'records_per_interval', e.target.value ? parseInt(e.target.value) : null)}
                                        min="1"
                                        max="10000"
                                        placeholder="Leave empty for no limit"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        Rate Limit (requests per minute)
                                        <Tooltip content="Maximum API requests per minute to avoid overwhelming the CRM">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.configuration.sync_settings.rate_limit?.requests_per_minute || 60}
                                        onChange={(e) => {
                                            const rateLimit = {
                                                ...formData.configuration.sync_settings.rate_limit,
                                                requests_per_minute: parseInt(e.target.value) || 60
                                            };
                                            updateConfiguration('sync_settings', 'rate_limit', rateLimit);
                                        }}
                                        min="1"
                                        max="1000"
                                        placeholder="60"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Error Display */}
                        {errors.general && (
                            <div style={{ 
                                marginTop: '15px', 
                                padding: '10px', 
                                backgroundColor: '#f8d7da', 
                                color: '#721c24', 
                                border: '1px solid #f5c6cb', 
                                borderRadius: '4px' 
                            }}>
                                {errors.general}
                            </div>
                        )}
                        
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowCreateModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleCreateTemplate}
                            >
                                Create Template
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Template Modal */}
            {showEditModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            padding: "20px",
                            borderRadius: "5px",
                            width: "80%",
                            maxWidth: "800px",
                            maxHeight: "90vh",
                            overflow: "auto",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                            <h2>Edit Integration Template</h2>
                            <button 
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "20px",
                                    cursor: "pointer",
                                    padding: "5px"
                                }}
                                onClick={() => setShowEditModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div>
                            {/* Same form fields as create modal */}
                            <div className="form-group">
                                <label>Template Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="e.g., slate_crm"
                                />
                                {errors.name && <span className="error">{errors.name}</span>}
                            </div>
                            <div className="form-group">
                                <label>Display Name *</label>
                                <input
                                    type="text"
                                    value={formData.display_name}
                                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                                    placeholder="e.g., Slate CRM Integration"
                                />
                                {errors.display_name && <span className="error">{errors.display_name}</span>}
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="Describe this integration template..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Version</label>
                                <input
                                    type="text"
                                    value={formData.version}
                                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                                    placeholder="1.0.0"
                                />
                            </div>
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>

                            {/* Configuration Sections */}
                            {renderAuthenticationFields()}

                            <div className="configuration-section">
                                <h3>Endpoints</h3>
                                <div className="form-group">
                                    <label>Base URL</label>
                                    <input
                                        type="url"
                                        value={formData.configuration.endpoints.base_url}
                                        onChange={(e) => updateConfiguration('endpoints', 'base_url', e.target.value)}
                                        placeholder="https://api.example.com"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Submit Data URL</label>
                                    <input
                                        type="text"
                                        value={formData.configuration.endpoints.submit_data.url}
                                        onChange={(e) => updateNestedConfiguration('endpoints', 'submit_data', 'url', e.target.value)}
                                        placeholder="/submit"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Test Connection URL</label>
                                    <input
                                        type="text"
                                        value={formData.configuration.endpoints.test_connection.url}
                                        onChange={(e) => updateNestedConfiguration('endpoints', 'test_connection', 'url', e.target.value)}
                                        placeholder="/test"
                                    />
                                </div>
                            </div>

                            {renderFieldMappingConfig()}

                            <div className="configuration-section">
                                <h3>
                                    Sync Settings
                                    <Tooltip content="Configure how data is synchronized with the CRM system">
                                        <span style={{ marginLeft: '8px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                    </Tooltip>
                                </h3>
                                
                                <div className="form-group">
                                    <label>
                                        Sync Mode
                                        <Tooltip content="Choose between single record processing or batch processing">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <select
                                        className="form-control"
                                        value={formData.configuration.sync_settings.mode || 'single'}
                                        onChange={(e) => updateConfiguration('sync_settings', 'mode', e.target.value)}
                                    >
                                        <option value="single">Single Record</option>
                                        <option value="batch">Batch Processing</option>
                                    </select>
                                </div>

                                {formData.configuration.sync_settings.mode === 'batch' && (
                                    <div className="form-group">
                                        <label>
                                            Batch Size
                                            <Tooltip content="Number of records to process in each batch">
                                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                            </Tooltip>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.configuration.sync_settings.batch_size || 50}
                                            onChange={(e) => updateConfiguration('sync_settings', 'batch_size', parseInt(e.target.value) || 50)}
                                            min="1"
                                            max="1000"
                                            placeholder="50"
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>
                                        Interval (minutes)
                                        <Tooltip content="How often to run sync operations">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.configuration.sync_settings.interval_minutes || 15}
                                        onChange={(e) => updateConfiguration('sync_settings', 'interval_minutes', parseInt(e.target.value) || 15)}
                                        min="1"
                                        max="1440"
                                        placeholder="15"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        Records Per Interval
                                        <Tooltip content="Maximum number of records to process per sync interval">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.configuration.sync_settings.records_per_interval || ''}
                                        onChange={(e) => updateConfiguration('sync_settings', 'records_per_interval', e.target.value ? parseInt(e.target.value) : null)}
                                        min="1"
                                        max="10000"
                                        placeholder="Leave empty for no limit"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        Rate Limit (requests per minute)
                                        <Tooltip content="Maximum API requests per minute to avoid overwhelming the CRM">
                                            <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                                        </Tooltip>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.configuration.sync_settings.rate_limit?.requests_per_minute || 60}
                                        onChange={(e) => {
                                            const rateLimit = {
                                                ...formData.configuration.sync_settings.rate_limit,
                                                requests_per_minute: parseInt(e.target.value) || 60
                                            };
                                            updateConfiguration('sync_settings', 'rate_limit', rateLimit);
                                        }}
                                        min="1"
                                        max="1000"
                                        placeholder="60"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Error Display */}
                        {errors.general && (
                            <div style={{ 
                                backgroundColor: '#f8d7da', 
                                color: '#721c24', 
                                padding: '10px', 
                                borderRadius: '4px', 
                                marginTop: '15px',
                                border: '1px solid #f5c6cb'
                            }}>
                                {errors.general}
                            </div>
                        )}
                        
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowEditModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={handleUpdateTemplate}
                            >
                                Update Template
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Template Modal */}
            {showViewModal && selectedTemplate && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            padding: "20px",
                            borderRadius: "5px",
                            width: "90%",
                            maxWidth: "1000px",
                            maxHeight: "90vh",
                            overflow: "auto",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                            <h2>{selectedTemplate.display_name}</h2>
                            <button 
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "20px",
                                    cursor: "pointer",
                                    padding: "5px"
                                }}
                                onClick={() => setShowViewModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div>
                            <div style={{ marginBottom: "20px" }}>
                                <div style={{ marginBottom: "20px" }}>
                                    <h3 style={{ borderBottom: "1px solid #ddd", paddingBottom: "10px", marginBottom: "15px" }}>Basic Information</h3>
                                    <div style={{ marginBottom: "8px" }}>
                                        <strong>Name:</strong> {selectedTemplate.name}
                                    </div>
                                    <div style={{ marginBottom: "8px" }}>
                                        <strong>Display Name:</strong> {selectedTemplate.display_name}
                                    </div>
                                    <div style={{ marginBottom: "8px" }}>
                                        <strong>Description:</strong> {selectedTemplate.description}
                                    </div>
                                    <div style={{ marginBottom: "8px" }}>
                                        <strong>Version:</strong> {selectedTemplate.version}
                                    </div>
                                    <div style={{ marginBottom: "8px" }}>
                                        <strong>Status:</strong> 
                                        <span style={{ 
                                            marginLeft: "5px",
                                            padding: "2px 8px",
                                            borderRadius: "3px",
                                            fontSize: "12px",
                                            fontWeight: "bold",
                                            backgroundColor: selectedTemplate.is_active ? "#d4edda" : "#f8d7da",
                                            color: selectedTemplate.is_active ? "#155724" : "#721c24"
                                        }}>
                                            {selectedTemplate.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ marginBottom: "20px" }}>
                                    <h3 style={{ borderBottom: "1px solid #ddd", paddingBottom: "10px", marginBottom: "15px" }}>Configuration</h3>
                                    <pre style={{ 
                                        backgroundColor: "#f8f9fa", 
                                        padding: "15px", 
                                        borderRadius: "5px", 
                                        overflow: "auto",
                                        fontSize: "12px",
                                        border: "1px solid #e9ecef"
                                    }}>
                                        {JSON.stringify(selectedTemplate.configuration, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowViewModal(false)}
                            >
                                Close
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={() => {
                                    setShowViewModal(false);
                                    openEditModal(selectedTemplate);
                                }}
                            >
                                Edit Template
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close" onClick={closeDeleteModal}>×</button>
                        <h2>Confirm Delete</h2>
                        <p>To delete the template <b>{templateToDelete?.display_name}</b>, type <b>delete</b> below and click Delete.</p>
                        <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={e => setDeleteConfirmText(e.target.value)}
                            placeholder="Type 'delete' to confirm"
                            style={{ width: '100%', padding: '8px', margin: '15px 0' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button className="btn btn-secondary" onClick={closeDeleteModal}>Cancel</button>
                            <button
                                className="btn btn-danger"
                                onClick={confirmDeleteTemplate}
                                disabled={deleteConfirmText !== 'delete'}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntegrationTemplateManagement; 